import { useEffect, useRef, useState } from "react";
import { createTestToneUrl } from "../../../lib/create-test-tone-url";
import { AudioLevelMeter } from "./audio-level-meter";
import s from "../conference-settings-modal.module.scss";

export const SpeakerDiagnostics = ({ deviceId, volume = 100 }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [level, setLevel] = useState(0);
  const [error, setError] = useState("");
  const teardownRef = useRef(() => {});
  const toneUrlRef = useRef("");

  useEffect(() => {
    return () => {
      teardownRef.current?.();

      if (toneUrlRef.current) {
        URL.revokeObjectURL(toneUrlRef.current);
      }
    };
  }, []);

  const stopTest = () => {
    teardownRef.current?.();
    teardownRef.current = () => {};
    setIsPlaying(false);
    setLevel(0);
  };

  const startTest = async () => {
    if (typeof window === "undefined") {
      return;
    }

    const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
    const AudioCtor = window.Audio;

    if (!AudioContextCtor || !AudioCtor) {
      setError("Браузер не поддерживает проверку динамика.");
      return;
    }

    setError("");
    stopTest();

    const context = new AudioContextCtor();
    const audioElement = new AudioCtor();
    let frameId = 0;
    let disposed = false;

    audioElement.preload = "auto";
    audioElement.playsInline = true;
    audioElement.crossOrigin = "anonymous";
    audioElement.volume = volume / 100;

    if (!toneUrlRef.current) {
      toneUrlRef.current = createTestToneUrl();
    }

    audioElement.src = toneUrlRef.current;

    if (deviceId && typeof audioElement.setSinkId === "function") {
      try {
        await audioElement.setSinkId(deviceId);
      } catch (_) {
        setError("Не удалось направить тест в выбранный динамик.");
      }
    } else if (deviceId && typeof audioElement.setSinkId !== "function") {
      setError("Этот браузер не позволяет выбрать конкретный динамик для теста.");
    }

    const cleanup = () => {
      if (disposed) {
        return;
      }

      disposed = true;
      window.cancelAnimationFrame(frameId);
      audioElement.pause();
      audioElement.currentTime = 0;
      void context.close();
    };

    teardownRef.current = cleanup;

    try {
      await context.resume();
      await audioElement.play();
      setIsPlaying(true);

      const updateLevel = () => {
        if (disposed) {
          return;
        }

        const progress = Math.min(audioElement.currentTime / Math.max(audioElement.duration || 1, 1), 1);
        const animatedLevel =
          (0.35 + Math.abs(Math.sin(progress * Math.PI * 8)) * 0.5) * (volume / 100);

        setLevel(Math.min(1, animatedLevel));
        frameId = window.requestAnimationFrame(updateLevel);
      };

      updateLevel();
      audioElement.onended = () => {
        cleanup();
        setIsPlaying(false);
        setLevel(0);
      };
    } catch (_) {
      cleanup();
      setIsPlaying(false);
      setLevel(0);
      setError("Не удалось воспроизвести тестовый сигнал. Проверьте разрешение на автозвук.");
    }
  };

  return (
    <div className={s.deviceDiagnostics}>
      <div className={s.deviceTestRow}>
				<button type="button" className={s.audioButton} onClick={isPlaying ? stopTest : startTest}>
          {isPlaying ? "Остановить" : "Проверить звук"}
        </button>
        <AudioLevelMeter
          level={level}
          status={isPlaying ? "active" : error ? "error" : "idle"}
          idleLabel="Нажмите кнопку, чтобы проверить динамик"
          activeLabel="Если звук есть, шкала должна двигаться"
          errorLabel={error}
        />
      </div>
    </div>
  );
};
